import {
  QR_PIN_MASK_HEIGHT,
  QR_PIN_MASK_PNG_BASE64,
  QR_PIN_MASK_WIDTH,
} from './qrPinMask';

export const MESSAGE_TOKENS = [
  {
    name: 'First Name',
    hint: 'Patient first name',
    example: 'Maria',
    source: 'patients.first_name',
    required: true,
  },
  {
    name: 'Last Name',
    hint: 'Patient last name',
    example: 'Garcia',
    source: 'patients.last_name',
  },
  {
    name: 'Full Name',
    hint: 'First and last name',
    example: 'Maria Garcia',
    source: 'patients.first_name + last_name',
  },
  {
    name: 'Account Name',
    hint: 'Pharmacy or brand name',
    example: 'Costco Stage',
    source: 'brands.name or accounts.name',
    aliases: ['Account', 'Store'],
    picker: 'account',
    required: true,
  },
  {
    name: 'Account Logo',
    hint: 'Brand logo from Accounts → Branding',
    example: 'logo.png',
    source: 'brands.logo_url',
    html: true,
  },
  {
    name: 'Location Name',
    hint: 'This store’s name',
    example: 'Jaimin Costco Bedside',
    source: 'boxes.name',
    aliases: ['Location', 'Box'],
    picker: 'location',
  },
  {
    name: 'Location Address',
    hint: 'This location’s street address',
    example: '100 Main St, Austin, TX',
    source: 'addresses',
  },
  {
    name: 'Location Phone',
    hint: 'Pharmacy phone number',
    example: '(512) 555-0100',
    source: 'brands.phone',
  },
  {
    name: 'Brand Phone',
    hint: 'Brand phone from the account',
    example: '(512) 555-0100',
    source: 'brands.phone',
  },
  {
    name: 'Brand Email',
    hint: 'Brand email from the account',
    example: 'pharmacy@example.com',
    source: 'brands.email',
  },
  {
    name: 'Pickup Code',
    hint: 'Order pickup code',
    example: 'ABC123',
    source: 'prescriptions_master.pucode',
    required: true,
  },
  {
    name: 'Order Summary',
    hint: 'Prescription table — medication, quantity, and prescriber. Total due lives in Payment Summary',
    example: 'Rx 10003421\nRx 10003422',
    source: 'prescription_detail',
    html: true,
  },
  {
    name: 'Hours',
    hint: 'One line for today, such as 9:00 am - 6:00 pm or Open all-day. Pharmacy Hours and Kiosk Hours are the full weekly schedules',
    example: 'Open 24/7',
    source: 'Hours of Operation',
  },
  {
    name: 'Pharmacy Hours',
    hint: 'Mon–Sun pharmacy hours plus lunch, from Accounts → Hours of Operation',
    example: '9:00 am - 6:00 pm',
    source: 'account_hours_of_operations',
    html: true,
  },
  {
    name: 'Kiosk Hours',
    hint: 'Mon–Sun kiosk hours. This location overrides the account default',
    example: 'Open all-day',
    source: 'kiosk_hours_of_operations',
    html: true,
  },
  {
    name: 'QR Code',
    hint: 'Pickup QR image, filled at send time',
    example: 'QR',
    source: 'pickup QR URL',
    html: true,
  },
  {
    name: 'Expiry Date',
    hint: 'How long the pickup code stays valid, from Accounts → Rx Expiration Days',
    example: '7 days',
    source: 'accounts.expiration_days',
  },
  {
    name: 'Website',
    hint: 'Pharmacy website',
    example: 'ilocalbox.com',
    source: 'brands.website',
  },
  {
    name: 'Link',
    hint: 'Pay now or I’m here link',
    example: 'ilocal.link/xxxx',
    source: 'short link',
    required: true,
  },
  {
    name: 'Amount',
    hint: 'Amount due, only if payment is allowed',
    example: '$12.40',
    source: 'order amount',
  },
  {
    name: 'Link TTL',
    hint: 'How long the link stays valid',
    example: '24 hours',
    source: 'link expiry',
  },
  {
    name: 'STOP',
    hint: 'Required SMS opt-out language',
    example: 'Reply STOP to end Messages.',
    source: 'required SMS language',
    required: true,
  },
  {
    name: 'OTP',
    hint: 'One-time passcode for staff or patient login',
    example: '482910',
    source: 'generated OTP',
  },
  {
    name: 'Username',
    hint: 'Staff login email',
    example: 'staff@pharmacy.com',
    source: 'users.username',
  },
  {
    name: 'Temporary Password',
    hint: 'Generated welcome or reset password',
    example: 'TempPass1!',
    source: 'generated credentials',
  },
];

const normalizeTokenName = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[_\s-]+/g, '');

export const findMessageToken = (value) => {
  const needle = normalizeTokenName(value);
  return (
    MESSAGE_TOKENS.find((token) => {
      const names = [token.name, token.name.replace(/\s+/g, '')].concat(
        token.aliases || []
      );
      return names.some((name) => normalizeTokenName(name) === needle);
    }) || null
  );
};

export const tokenNeedsStorePicker = (token) =>
  !!(token && (token.picker === 'account' || token.picker === 'location'));

const QR_PIN_HEX = /^#[0-9a-fA-F]{6}$/;

export const qrPinDataUri = (color) => {
  const fill = QR_PIN_HEX.test(String(color || '').trim()) ? String(color).trim() : '#00a7e1';
  const [r, g, b] = [1, 3, 5].map(
    (n) => (255 - parseInt(fill.slice(n, n + 2), 16)) / 255
  );
  const w = QR_PIN_MASK_WIDTH;
  const h = QR_PIN_MASK_HEIGHT;
  const href = `data:image/png;base64,${QR_PIN_MASK_PNG_BASE64}`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${w} ${h}"><filter id="p" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="${-r} 0 0 0 1 ${-g} 0 0 0 1 ${-b} 0 0 0 1 0 0 0 1 0"/></filter><image width="${w}" height="${h}" xlink:href="${href}" href="${href}" filter="url(#p)"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export const buildSampleQrHtml = (color) => {
  const src =
    'https://api.qrserver.com/v1/create-qr-code/?size=148x148&amp;ecc=H&amp;margin=1&amp;data=8E8QVW';
  const logo = qrPinDataUri(color);
  return `<div data-qr-mark="1" style="position:relative;width:148px;height:148px;margin:14px auto 0;">
<img data-qr-src="1" src="${src}" alt="Pickup QR" width="148" height="148" style="display:block;width:148px;height:148px;max-width:148px;border:0;outline:none;" />
<img data-brand="qr-mark" src="${logo}" alt="" width="30" height="40" style="position:absolute;left:50%;top:50%;width:30px;height:40px;max-width:30px;margin:-23px 0 0 -18px;background:#ffffff;border-radius:8px;padding:3px;display:block;box-sizing:content-box;" />
</div>`;
};

export const SAMPLE_QR_HTML = buildSampleQrHtml('#00a7e1');

export const QR_CODE_TOKEN_HTML =
  '<span class="mceNonEditable" data-locked="false" data-token="QR Code">{{QR Code}}</span>';

export const serializeQrCode = (html) =>
  String(html || '')
    .replace(
      /<div\b[^>]*data-token=["']QR Code["'][^>]*>[\s\S]*?<\/div>/gi,
      QR_CODE_TOKEN_HTML
    )
    .replace(
      /<div\b[^>]*data-qr-mark=["']1["'][^>]*>[\s\S]*?<\/div>/gi,
      QR_CODE_TOKEN_HTML
    )
    .replace(
      /<img\b[^>]*data-token=["']QR Code["'][^>]*\/?>/gi,
      QR_CODE_TOKEN_HTML
    )
    .replace(/<img\b[^>]*data-qr-src=["']1["'][^>]*\/?>/gi, '')
    .replace(/<img\b[^>]*data-brand=["']qr-mark["'][^>]*\/?>/gi, '');

export const injectQrCode = (html, color) => {
  const source = String(html || '');
  if (!source.trim()) {
    return source;
  }
  const image = buildSampleQrHtml(color || '#00a7e1').replace(
    '<div data-qr-mark="1"',
    '<div class="mceNonEditable" data-token="QR Code" data-qr-mark="1"'
  );
  return source
    .replace(/<div\b[^>]*data-qr-mark=["']1["'][^>]*>[\s\S]*?<\/div>/gi, image)
    .replace(
      /<img\b[^>]*data-token=["']QR Code["'][^>]*\/?>/gi,
      image
    )
    .replace(
      /<(span|p)\b[^>]*data-token=["']QR Code["'][^>]*>[\s\S]*?<\/\1>/gi,
      image
    )
    .replace(/\{\{\s*QR Code\s*\}\}/gi, image);
};

export const formatExpiryInDays = (days) => {
  const n = parseInt(days, 10);
  if (!Number.isFinite(n) || n <= 0) {
    return '7 days';
  }
  return n === 1 ? '1 day' : `${n} days`;
};

const hoursSampleRow = (day, value) =>
  `<tr><td style="font-family:Helvetica, Arial, sans-serif;font-size:13px;color:#64748b;padding:3px 0;">${day}</td><td align="right" style="font-family:Helvetica, Arial, sans-serif;font-size:13px;font-weight:bold;text-align:right;color:#1e3a5f;padding:3px 0;">${value}</td></tr>`;

export const SAMPLE_PHARMACY_HOURS_HTML = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;">
${hoursSampleRow('Monday', '9:00 am - 6:00 pm')}
${hoursSampleRow('Tuesday', '9:00 am - 6:00 pm')}
${hoursSampleRow('Wednesday', '9:00 am - 6:00 pm')}
${hoursSampleRow('Thursday', '9:00 am - 6:00 pm')}
${hoursSampleRow('Friday', '9:00 am - 6:00 pm')}
${hoursSampleRow('Saturday', '9:00 am - 1:00 pm')}
${hoursSampleRow('Sunday', 'Closed')}
<tr><td colspan="2" style="font-family:Helvetica, Arial, sans-serif;font-size:12px;color:#64748b;padding:8px 0 0;border-top:1px solid #e2e8f0;">Lunch Hours: 12:30 pm - 1:00 pm</td></tr>
</table>`;

export const SAMPLE_KIOSK_HOURS_HTML = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;">
${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  .map((day) => hoursSampleRow(day, 'Open all-day'))
  .join('')}
</table>`;

export {
  SAMPLE_ORDER_SUMMARY_HTML,
  SAMPLE_ORDER_SUMMARY_HTML_NO_AMOUNT,
  SAMPLE_ORDER_SUMMARY_TEXT,
  SAMPLE_ORDER_SUMMARY_TEXT_NO_AMOUNT,
  buildSampleOrderSummaryHtml,
  buildSampleOrderSummaryText,
} from './orderSummary';

export const tokenWidgetHtml = (name, locked = false) =>
  `<span class="mceNonEditable" data-locked="${locked ? 'true' : 'false'}" data-token="${name}">{{${name}}}</span>`;



// WEBPACK FOOTER //
// ./src/components/message-control-center/tokens.js